'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { useIsMobile } from '../../hooks/use-mobile';
import { useVisualViewportRect } from '../../hooks/use-visual-viewport-rect';
import { usePortalContainer } from '../../lib/portal-container';
import { cn } from '../../lib/utils';
function Dialog({ ...props }) {
    return _jsx(DialogPrimitive.Root, { "data-slot": "dialog", ...props });
}
function DialogClose({ ...props }) {
    return _jsx(DialogPrimitive.Close, { "data-slot": "dialog-close", ...props });
}
/**
 * Le contenu plein écran, en mobile, défile dans un bloc INTÉRIEUR : la croix de
 * fermeture est positionnée en `absolute` sur le panneau, et dans un conteneur
 * qui défile elle défilerait avec le contenu — sur téléphone il ne resterait
 * alors aucune sortie visible (pas d'overlay à côté, pas de touche Échap).
 *
 * Le pied reste collé en bas de ce bloc : c'est là que se trouve le geste que le
 * dialogue demande, et il ne doit jamais partir sous le clavier.
 */
const FULL_SCREEN_BODY_CLASSES = [
    'flex flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4 pt-5',
    // Le titre passe sous la croix sans cette réserve, et cadré à gauche il se lit
    // comme un en-tête de page plutôt que comme une étiquette centrée sous elle.
    '[&>[data-slot=dialog-header]]:pr-10 [&>[data-slot=dialog-header]]:text-left',
    '[&>[data-slot=dialog-footer]]:bg-background [&>[data-slot=dialog-footer]]:sticky',
    '[&>[data-slot=dialog-footer]]:-mx-4 [&>[data-slot=dialog-footer]]:mt-auto',
    '[&>[data-slot=dialog-footer]]:bottom-0 [&>[data-slot=dialog-footer]]:border-t',
    '[&>[data-slot=dialog-footer]]:px-4 [&>[data-slot=dialog-footer]]:py-3',
    '[&>[data-slot=dialog-footer]_button]:w-full sm:[&>[data-slot=dialog-footer]_button]:w-auto',
].join(' ');
function DialogContent({ className, children, fullScreenOnMobile = false, onOverlayClick, showCloseButton = true, ...props }) {
    const isMobile = useIsMobile();
    const isFullScreen = fullScreenOnMobile && isMobile;
    // Le clavier ne réduit pas le viewport de MISE EN PAGE : `inset-0` couvrirait
    // encore tout l'écran, panneau à moitié caché sous le clavier. On se cale sur
    // le viewport VISUEL, comme `FullScreenOverlay`.
    const viewportRect = useVisualViewportRect(isFullScreen);
    const viewportStyle = isFullScreen && viewportRect
        ? {
            bottom: 'auto',
            height: `${viewportRect.height}px`,
            left: `${viewportRect.offsetLeft}px`,
            maxHeight: `${viewportRect.height}px`,
            right: 'auto',
            top: `${viewportRect.offsetTop}px`,
            width: `${viewportRect.width}px`,
        }
        : undefined;
    return (_jsxs(DialogPortal, { "data-slot": "dialog-portal", children: [onOverlayClick ? (_jsx("div", { className: "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0", "data-slot": "dialog-overlay", onClick: onOverlayClick })) : (_jsx(DialogOverlay, {})), _jsxs(DialogPrimitive.Content, { className: cn('bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50 shadow-xl duration-200', isFullScreen
                    ? 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom fixed inset-0 flex h-dvh max-h-dvh w-screen max-w-full flex-col gap-0 rounded-none p-0'
                    : 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 ring-border/30 fixed top-[50%] left-[50%] grid w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg p-6 ring-1', className), "data-slot": "dialog-content", style: viewportStyle, ...props, children: [isFullScreen ? _jsx("div", { className: FULL_SCREEN_BODY_CLASSES, children: children }) : children, showCloseButton && (_jsxs(DialogPrimitive.Close, { className: "absolute top-4 right-4 z-10 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", "data-slot": "dialog-close", children: [_jsx(XIcon, {}), _jsx("span", { className: "sr-only", children: "Close" })] }))] })] }));
}
function DialogDescription({ className, ...props }) {
    return (_jsx(DialogPrimitive.Description, { className: cn('text-muted-foreground text-sm', className), "data-slot": "dialog-description", ...props }));
}
function DialogFooter({ className, ...props }) {
    return (_jsx("div", { className: cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className), "data-slot": "dialog-footer", ...props }));
}
function DialogHeader({ className, ...props }) {
    return (_jsx("div", { className: cn('flex flex-col gap-2 text-center sm:text-left', className), "data-slot": "dialog-header", ...props }));
}
function DialogOverlay({ className, ...props }) {
    return (_jsx(DialogPrimitive.Overlay, { className: cn('data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50', className), "data-slot": "dialog-overlay", ...props }));
}
function DialogPortal({ ...props }) {
    const portalContainer = usePortalContainer();
    return (_jsx(DialogPrimitive.Portal, { container: portalContainer ?? undefined, "data-slot": "dialog-portal", ...props }));
}
function DialogTitle({ className, ...props }) {
    return (_jsx(DialogPrimitive.Title, { className: cn('text-lg leading-none font-semibold', className), "data-slot": "dialog-title", ...props }));
}
function DialogTrigger({ ...props }) {
    return _jsx(DialogPrimitive.Trigger, { "data-slot": "dialog-trigger", ...props });
}
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, };
